// Full bilingual content (EN / DE) for the Metakaolin knowledge site by Newchem GmbH.
// NOTE: This is MOCK/static content resolved on the client. No backend involved.

export const translations = {
  en: {
    nav: {
      home: 'Overview',
      quality: 'Quality',
      features: 'Features',
      reaction: 'Reaction',
      applications: 'Applications',
      downloads: 'Downloads',
      cases: 'Case studies',
      links: 'Links',
      disclaimer: 'Disclaimer',
      contact: 'Contact',
    },
    common: {
      byNewchem: 'A knowledge platform by Newchem GmbH',
      contactCta: 'Talk to our experts',
      requestInfo: 'Request information',
      learnMore: 'Learn more',
      backHome: 'Back to overview',
      langLabel: 'EN',
      visitNewchem: 'Visit newchem.info',
    },
    hero: {
      badge: 'The pozzolanic additive for high-performance concrete & mortar',
      titleA: 'Everything you need to know about',
      titleAccent: 'metakaolin',
      titleB: 'in modern construction chemistry.',
      subtitle:
        'Metakaolin is a highly reactive pozzolanic additive that improves the durability, strength and appearance of concrete, mortar and lime systems. This platform, maintained by Newchem GmbH, gathers the essential technical knowledge in one place.',
      pills: ['Pozzolanic reactivity', 'Higher durability', 'Lower CO\u2082 footprint', 'Brighter surfaces'],
      primaryCta: 'Explore the technology',
      secondaryCta: 'Contact Newchem',
    },
    intro: {
      label: 'General description',
      title: 'A valuable admixture for concrete and cement',
      p1: 'Metakaolin is a pozzolanic additive/product which can provide many specific features. It is available in many different varieties and qualities \u2014 the purity defines the binding capacity for free lime, and some grades also provide a special reactivity.',
      p2: 'Metakaolin is a valuable admixture for concrete and/or cement applications. Usually 8\u201320% (by weight) of Portland cement is replaced by metakaolin. Such a concrete exhibits favourable engineering properties.',
      p3: 'The pozzolanic reaction starts soon after mixing and continues between 7 and 28 days, gradually converting free lime into stable, strength-giving hydrate phases.',
      stats: [
        { value: '8\u201320%', label: 'Typical cement replacement by weight' },
        { value: '7\u201328 d', label: 'Duration of the pozzolanic reaction' },
        { value: '\u226440%', label: 'Al\u2082O\u2083 content in high-grade metakaolin' },
      ],
    },
    what: {
      label: 'Why metakaolin',
      title: 'Engineered performance for demanding applications',
      subtitle:
        'From high-durability infrastructure to bright architectural concrete and historical lime restoration \u2014 metakaolin delivers measurable, repeatable benefits.',
      cards: [
        {
          title: 'Reacts with free lime',
          desc: 'The pozzolanic reaction binds calcium hydroxide (portlandite) and forms additional CSH phases, densifying the microstructure.',
        },
        {
          title: 'Improves durability',
          desc: 'Finer pore structure reduces permeability, chloride migration and the risk of alkali\u2013silica reaction (ASR).',
        },
        {
          title: 'Enhances appearance',
          desc: 'A lighter colour and reduced efflorescence make metakaolin ideal for visible and architectural concrete.',
        },
      ],
    },
    newchem: {
      label: 'The company behind this platform',
      title: 'Newchem GmbH \u2014 independent Austrian additive specialists',
      p1: 'This knowledge platform is created and maintained by Newchem GmbH, an independent, family-run Austrian innovator and distributor of high-performance admixtures, supplementary cementitious materials and engineered binder systems \u2014 present in more than 40 countries across Europe.',
      p2: 'For selection, dosage, mix-design and supply of the right metakaolin grade for your application, our technical team is ready to help.',
      pills: ['T\u00dcV S\u00fcd certified', 'ISO 9001:2015', 'Active in 40+ countries', 'Independent \u00b7 Family-run'],
      cta: 'Visit newchem.info',
    },
    quality: {
      label: 'Quality criteria',
      title: 'What defines a high-quality metakaolin',
      lead:
        'Whenever metakaolin reacts with lime and cement, the following quality criteria determine its performance in the final product.',
      image: 'labTest1',
      criteria: [
        {
          title: 'Lime-binding capacity',
          desc: 'The amount of free lime the metakaolin is able to bind, commonly assessed with the Chapelle test. A higher value indicates stronger pozzolanic activity.',
        },
        {
          title: 'Reactivity & specific surface',
          desc: 'The velocity at which hydrated lime is consumed. Fineness and specific surface (e.g. BET) strongly influence early reactivity.',
        },
        {
          title: 'Mechanical properties',
          desc: 'The resulting compressive and flexural strength development of mortars and concrete containing the metakaolin.',
        },
        {
          title: 'Colour',
          desc: 'The colour of the metakaolin directly influences the colour of the final product \u2014 crucial for white and architectural concrete.',
        },
      ],
    },
    features: {
      label: 'Specific features',
      title: 'The specific features metakaolin can provide',
      lead:
        'Depending on grade and dosage, metakaolin contributes a broad range of technical benefits to concrete, mortar and lime systems.',
      list: [
        { title: 'Pore size', desc: 'Can contribute to refining the pore structure and thereby to a denser binder matrix.' },
        { title: 'Durability', desc: 'Can, with a suitable mix design, support the density and durability behaviour of cementitious systems.' },
        { title: 'Adhesion strength', desc: 'Can influence the bond (tensile adhesion) strength in selected render, adhesive and repair-mortar systems.' },
        { title: 'Water retention', desc: 'Can change water demand and rheology; the effect on water retention and workability is system-dependent.' },
        { title: 'ASR reduction', desc: 'Can, with a suitable composition, contribute to reducing the ASR risk; this must be demonstrated by appropriate test methods.' },
        { title: 'Sulphate resistance', desc: 'Can improve sulphate-resistance behaviour in suitable binder systems. The assessment must relate to the binder, the sulphate medium and the exposure conditions.' },
        { title: 'Efflorescence', desc: 'Can reduce the risk of efflorescence in suitable systems.' },
        { title: 'Chloride migration', desc: 'Can reduce chloride ingress through a refined pore structure, provided the mix design and curing are tailored accordingly.' },
        { title: 'Lighter colour', desc: 'Can enable light to very light surfaces, depending on raw-material colour and the overall mix design.' },
        { title: 'Freeze / thaw resistance', desc: 'Can contribute to freeze\u2013thaw resistance in suitable, sufficiently dense and air-entrained systems.' },
        { title: 'Acid resistance', desc: 'Can influence resistance to limited acidic exposure in certain binder systems. Suitability testing for the specific medium is required.' },
        { title: 'Reducing CO\u2082', desc: 'Can contribute to reducing the accounted CO\u2082 emissions of a binder system through clinker substitution. The actual effect depends on the system and the accounting method.' },
        { title: 'Setting time', desc: 'Can influence setting behaviour. The effect depends on the binder, dosage, admixtures and temperature.' },
      ],
    },
    reaction: {
      label: 'Reaction with cement & lime',
      title: 'How metakaolin reacts with cement and lime',
      lead:
        'Portland cement consists of about 80% calcium silicates \u2014 alite (C\u2083S) and belite (C\u2082S). Understanding their hydration explains why the pozzolanic reaction of metakaolin is so beneficial.',
      image: 'powderMagnifier',
      block1Title: 'Hydration of Portland cement',
      block1Body:
        'Hydration of the clinker phases mainly forms C-S-H phases and portlandite (calcium hydroxide, CH). The type and proportions of the hydration products depend on the cement composition, the water-to-binder ratio, the hydration age and any supplementary materials.',
      eq1: ['C\u2083S + 3H \u2192 CSH + 2 CH', 'C\u2082S + 2H \u2192 CSH + CH'],
      block2Title: 'The pozzolanic reaction',
      block2Body:
        'The calcium hydroxide (portlandite, CH) formed can react pozzolanically with metakaolin. Besides C-S-H, alumina-bearing hydrate phases can form \u2013 such as C-A-S-H phases as well as calcium aluminate and calcium sulfoaluminate hydrates like str\u00e4tlingite.',
      eq2: ['CH + S \u2192 CSH   (gel-like structures)', 'CH + S + A \u2192 C\u2082ASH\u2088   (e.g. str\u00e4tlingite)'],
      schematicNote: 'Simplified, schematic representation; the hydrate phases actually formed depend on the chemical composition and the reaction conditions.',
      note:
        'Through the pozzolanic reaction, metakaolin can convert part of the portlandite formed during cement hydration into additional hydrate phases. This can refine the pore structure and \u2013 depending on mix design, dosage and curing \u2013 positively influence strength and durability.',
    },
    applications: {
      label: 'Applications',
      title: 'Metakaolin applications around the globe',
      lead:
        'From large-scale infrastructure to fine architectural finishes and heritage restoration \u2014 metakaolin is used across the full spectrum of cement- and lime-based systems.',
      groups: [
        { title: 'Cement', items: ['Support of early-strength development in suitable mixes', 'Reduced efflorescence risk in suitable systems', 'Binder systems with potential for increased durability'] },
        { title: 'Cast-in-place concrete', items: ['Mixes for chloride- and salt-exposed environments \u2013 subject to application-related suitability testing', 'Road pavements', 'Infrastructure applications with increased durability requirements \u2013 subject to project-specific testing'] },
        { title: 'Precast concrete', items: ['Light to very light concrete surfaces \u2013 depending on raw-material colour and mix design', 'Coloured visible concrete', 'Selected precast applications subject to in-house testing'] },
        { title: 'Shotcrete', items: ['Special mortars and shotcrete systems for selected infrastructure projects', 'Slope-stabilisation and repair systems \u2013 subject to project-specific suitability testing', 'Repair and strengthening mortars subject to application-specific validation'] },
        {
          title: 'Mortars',
          items: [
            'Repair mortars',
            'Historical lime mortars',
            'Cement / lime renders',
            'Pure lime renders',
            'Adhesives',
            'Thermal insulation systems',
            'Efflorescence-free mortars',
            'Swimming pools',
            'Water-tight coatings',
            'Tile grouts',
          ],
        },
        { title: 'Geopolymers', items: ['Alkali-activated binders', 'Clinker-reduced or alkali-activated binder systems; the CO\u2082 balance is system-dependent', 'Alkali-activated and high-temperature-resistant special binders \u2013 depending on mix design and testing requirements'] },
      ],
    },
    cases: {
      label: 'Case studies',
      title: 'Metakaolin in real-world projects',
      lead:
        'Selected examples illustrating how metakaolin is applied in practice. (Representative examples for illustration.)',
      list: [
        {
          title: 'White architectural facade',
          location: 'Central Europe',
          desc: 'Precast facade panels with metakaolin delivered a bright, uniform surface free of efflorescence and with excellent long-term durability.',
        },
        {
          title: 'Marine infrastructure',
          location: 'Atlantic coast',
          desc: 'Cast-in-place elements in a splash zone benefited from strongly reduced chloride migration and improved sulphate resistance.',
        },
        {
          title: 'Heritage lime restoration',
          location: 'Historic city centre',
          desc: 'Pure lime renders modified with metakaolin restored a listed building while remaining compatible with the original substrate.',
        },
        {
          title: 'Swimming pool waterproofing',
          location: 'Southern Europe',
          desc: 'Water-tight coatings with metakaolin achieved a dense, durable finish resistant to continuous water exposure.',
        },
      ],
    },
    links: {
      label: 'External links',
      title: 'Further reading & references',
      lead: 'Curated external resources on pozzolanic reactions, cement chemistry and metakaolin.',
      list: [
        { label: 'Pozzolanic reaction', url: 'https://en.wikipedia.org/wiki/Pozzolanic_reaction', desc: 'Wikipedia \u2014 the chemistry behind pozzolanic additives.' },
        { label: 'Portland cement', url: 'https://en.wikipedia.org/wiki/Portland_cement', desc: 'Wikipedia \u2014 composition and hydration of Portland cement.' },
        { label: 'Calcium silicate hydrate (CSH)', url: 'https://en.wikipedia.org/wiki/Calcium_silicate_hydrate', desc: 'Wikipedia \u2014 the main strength-giving phase in concrete.' },
        { label: 'Calcium hydroxide (portlandite)', url: 'https://en.wikipedia.org/wiki/Calcium_hydroxide', desc: 'Wikipedia \u2014 the free lime consumed by the pozzolanic reaction.' },
        { label: 'Concrete', url: 'https://en.wikipedia.org/wiki/Concrete', desc: 'Wikipedia \u2014 general overview of concrete as a material.' },
        { label: 'Newchem GmbH', url: 'https://www.newchem.info', desc: 'The additive specialists behind this platform.' },
      ],
    },
    disclaimer: {
      label: 'Disclaimer',
      title: 'Disclaimer',
      body: [
        'The information provided on this website about metakaolin is of a general, informative nature and is compiled to the best of our knowledge and belief.',
        'It does not constitute a guarantee of specific properties or suitability for a particular application. Values such as dosage and performance depend on the specific metakaolin grade, the binder system, the mix design and the site conditions.',
        'Users must verify the suitability of any product for their own application through their own testing. No liability is accepted for damages arising from the use of this information.',
        'For binding technical advice, product selection and supply, please contact Newchem GmbH directly.',
      ],
    },
    contact: {
      label: 'Contact',
      title: 'Talk to the Newchem team',
      lead:
        'For technical advice on metakaolin grades, dosage, mix-design and supply, get in touch with our specialists in Austria.',
      formTitle: 'Send us a message',
      name: 'Name',
      email: 'Email',
      company: 'Company',
      message: 'Your message',
      send: 'Send message',
      sent: 'Thank you! Your message has been recorded.',
      officeTitle: 'Newchem GmbH',
      addressTitle: 'Address',
      emailTitle: 'Email',
      phoneTitle: 'Phone',
      webTitle: 'Website',
    },
    footer: {
      tagline: 'The knowledge platform on metakaolin \u2014 with editorial oversight and support by Newchem GmbH.',
      quickLinks: 'Navigation',
      contactTitle: 'Contact',
      rights: 'All rights reserved.',
      builtBy: 'A platform by',
      legal: 'Legal',
    },
  },

  de: {
    nav: {
      home: '\u00dcbersicht',
      quality: 'Qualit\u00e4t',
      features: 'Eigenschaften',
      reaction: 'Reaktion',
      applications: 'Anwendungen',
      downloads: 'Downloads',
      cases: 'Fallstudien',
      links: 'Links',
      disclaimer: 'Haftung',
      contact: 'Kontakt',
    },
    common: {
      byNewchem: 'Eine Wissensplattform der Newchem GmbH',
      contactCta: 'Sprechen Sie mit unseren Experten',
      requestInfo: 'Informationen anfordern',
      learnMore: 'Mehr erfahren',
      backHome: 'Zur\u00fcck zur \u00dcbersicht',
      langLabel: 'DE',
      visitNewchem: 'newchem.info besuchen',
    },
    hero: {
      badge: 'Das puzzolanische Additiv f\u00fcr Hochleistungsbeton & -m\u00f6rtel',
      titleA: 'Alles Wissenswerte \u00fcber',
      titleAccent: 'Metakaolin',
      titleB: 'in der modernen Bauchemie.',
      subtitle:
        'Metakaolin ist ein hochreaktives puzzolanisches Additiv, das die Dauerhaftigkeit, Festigkeit und Optik von Beton, M\u00f6rtel und Kalksystemen verbessert. Diese Plattform der Newchem GmbH b\u00fcndelt das wesentliche Fachwissen an einem Ort.',
      pills: ['Puzzolanische Reaktivit\u00e4t', 'H\u00f6here Dauerhaftigkeit', 'Geringerer CO\u2082-Fu\u00dfabdruck', 'Hellere Oberfl\u00e4chen'],
      primaryCta: 'Technologie entdecken',
      secondaryCta: 'Newchem kontaktieren',
    },
    intro: {
      label: 'Allgemeine Beschreibung',
      title: 'Ein wertvoller Zusatzstoff f\u00fcr Beton und Zement',
      p1: 'Metakaolin ist ein puzzolanisches Additiv/Produkt, das zahlreiche besondere Eigenschaften erm\u00f6glicht. Es ist in vielen Varianten und Qualit\u00e4ten verf\u00fcgbar \u2014 die Reinheit bestimmt die Bindekapazit\u00e4t f\u00fcr freien Kalk, und einige Qualit\u00e4ten bieten zus\u00e4tzlich eine besondere Reaktivit\u00e4t.',
      p2: 'Metakaolin ist ein wertvoller Zusatzstoff f\u00fcr Beton- und/oder Zementanwendungen. \u00dcblicherweise werden 8\u201320 % (Gewichtsanteil) des Portlandzements durch Metakaolin ersetzt. Ein solcher Beton weist g\u00fcnstige technische Eigenschaften auf.',
      p3: 'Die puzzolanische Reaktion beginnt kurz nach dem Anmischen und setzt sich \u00fcber 7 bis 28 Tage fort, wobei freier Kalk schrittweise in stabile, festigkeitsbildende Hydratphasen umgewandelt wird.',
      stats: [
        { value: '8\u201320 %', label: 'Typischer Zementersatz nach Gewicht' },
        { value: '7\u201328 T', label: 'Dauer der puzzolanischen Reaktion' },
        { value: '\u226440 %', label: 'Al\u2082O\u2083-Gehalt in hochwertigem Metakaolin' },
      ],
    },
    what: {
      label: 'Warum Metakaolin',
      title: 'Technische Leistung f\u00fcr anspruchsvolle Anwendungen',
      subtitle:
        'Von dauerhafter Infrastruktur \u00fcber hellen Sichtbeton bis zur historischen Kalkrestaurierung \u2014 Metakaolin liefert messbare, reproduzierbare Vorteile.',
      cards: [
        {
          title: 'Reagiert mit freiem Kalk',
          desc: 'Die puzzolanische Reaktion bindet Calciumhydroxid (Portlandit) und bildet zus\u00e4tzliche CSH-Phasen, die das Gef\u00fcge verdichten.',
        },
        {
          title: 'Erh\u00f6ht die Dauerhaftigkeit',
          desc: 'Ein feineres Porengef\u00fcge senkt Durchl\u00e4ssigkeit, Chloridmigration und das Risiko der Alkali-Kiesel-Reaktion (AKR).',
        },
        {
          title: 'Verbessert die Optik',
          desc: 'Hellere Farbe und reduzierte Ausbl\u00fchungen machen Metakaolin ideal f\u00fcr Sicht- und Architekturbeton.',
        },
      ],
    },
    newchem: {
      label: 'Das Unternehmen hinter dieser Plattform',
      title: 'Newchem GmbH \u2014 unabh\u00e4ngige \u00f6sterreichische Additiv-Spezialisten',
      p1: 'Diese Wissensplattform wird von der Newchem GmbH erstellt und gepflegt \u2014 einem unabh\u00e4ngigen, familiengef\u00fchrten \u00f6sterreichischen Innovator und Distributor von Hochleistungs-Zusatzmitteln, zusatzstofflichen Bindemitteln und ma\u00dfgeschneiderten Bindersystemen, pr\u00e4sent in \u00fcber 40 L\u00e4ndern Europas.',
      p2: 'F\u00fcr Auswahl, Dosierung, Rezepturentwicklung und Lieferung der richtigen Metakaolin-Qualit\u00e4t f\u00fcr Ihre Anwendung steht Ihnen unser technisches Team gerne zur Verf\u00fcgung.',
      pills: ['T\u00dcV S\u00fcd zertifiziert', 'ISO 9001:2015', 'In 40+ L\u00e4ndern aktiv', 'Unabh\u00e4ngig \u00b7 Familienbetrieb'],
      cta: 'newchem.info besuchen',
    },
    quality: {
      label: 'Qualit\u00e4tskriterien',
      title: 'Was ein hochwertiges Metakaolin ausmacht',
      lead:
        'Wenn Metakaolin mit Kalk und Zement reagiert, sind die folgenden Qualit\u00e4tskriterien entscheidend f\u00fcr die Leistung im Endprodukt.',
      image: 'labTest1',
      criteria: [
        {
          title: 'Kalkbindeverm\u00f6gen',
          desc: 'Die Menge an freiem Kalk, die das Metakaolin binden kann \u2014 h\u00e4ufig mit dem Chapelle-Test bestimmt. Ein h\u00f6herer Wert bedeutet st\u00e4rkere puzzolanische Aktivit\u00e4t.',
        },
        {
          title: 'Reaktivit\u00e4t & spezifische Oberfl\u00e4che',
          desc: 'Die Geschwindigkeit, mit der der Kalkhydrat verbraucht wird. Feinheit und spezifische Oberfl\u00e4che (z. B. BET) beeinflussen die fr\u00fche Reaktivit\u00e4t stark.',
        },
        {
          title: 'Mechanische Eigenschaften',
          desc: 'Die resultierende Druck- und Biegezugfestigkeitsentwicklung von M\u00f6rteln und Beton mit dem Metakaolin.',
        },
        {
          title: 'Farbe',
          desc: 'Die Farbe des Metakaolins beeinflusst direkt die Farbe des Endprodukts \u2014 entscheidend f\u00fcr wei\u00dfen und Architekturbeton.',
        },
      ],
    },
    features: {
      label: 'Besondere Eigenschaften',
      title: 'Die besonderen Eigenschaften, die Metakaolin bietet',
      lead:
        'Je nach Qualit\u00e4t und Dosierung tr\u00e4gt Metakaolin zu einer breiten Palette technischer Vorteile in Beton-, M\u00f6rtel- und Kalksystemen bei.',
      list: [
        { title: 'Porengr\u00f6\u00dfe', desc: 'Kann zur Verfeinerung der Porenstruktur und damit zu einer dichteren Bindemittelmatrix beitragen.' },
        { title: 'Dauerhaftigkeit', desc: 'Kann bei geeigneter Rezeptur die Dichtheit und das Dauerhaftigkeitsverhalten zement\u00e4rer Systeme unterst\u00fctzen.' },
        { title: 'Haftfestigkeit', desc: 'Kann die Haftzugfestigkeit in ausgew\u00e4hlten Putz-, Kleber- und Reparaturm\u00f6rtelsystemen beeinflussen.' },
        { title: 'Wasserr\u00fcckhaltung', desc: 'Kann Wasseranspruch und Rheologie ver\u00e4ndern; die Auswirkung auf Wasserr\u00fcckhaltung und Verarbeitbarkeit ist systemabh\u00e4ngig.' },
        { title: 'AKR-Reduktion', desc: 'Kann bei geeigneter Zusammensetzung zur Verringerung des AKR-Risikos beitragen; dies ist \u00fcber geeignete Pr\u00fcfverfahren nachzuweisen.' },
        { title: 'Sulfatbest\u00e4ndigkeit', desc: 'Kann in geeigneten Bindemittelsystemen das Sulfatwiderstandsverhalten verbessern. Die Bewertung muss bezogen auf Bindemittel, Sulfatmedium und Expositionsbedingungen erfolgen.' },
        { title: 'Ausbl\u00fchungen', desc: 'Kann das Risiko von Ausbl\u00fchungen in geeigneten Systemen reduzieren.' },
        { title: 'Chloridmigration', desc: 'Kann durch eine verfeinerte Porenstruktur das Eindringen von Chloriden reduzieren, sofern die Rezeptur und Nachbehandlung darauf abgestimmt sind.' },
        { title: 'Hellere Farbe', desc: 'Kann abh\u00e4ngig von Rohstofffarbe und Gesamt-Rezeptur helle bis sehr helle Oberfl\u00e4chen erm\u00f6glichen.' },
        { title: 'Frost-Tau-Best\u00e4ndigkeit', desc: 'Kann in geeigneten, ausreichend dichten und luftporenoptimierten Systemen zur Frost-Tau-Best\u00e4ndigkeit beitragen.' },
        { title: 'S\u00e4urebest\u00e4ndigkeit', desc: 'Kann in bestimmten Bindemittelsystemen die Widerstandsf\u00e4higkeit gegen\u00fcber begrenzter S\u00e4urebeanspruchung beeinflussen. Eine Eignungspr\u00fcfung f\u00fcr das jeweilige Medium ist erforderlich.' },
        { title: 'CO\u2082-Reduktion', desc: 'Kann durch Klinkersubstitution zur Verringerung der bilanzierten CO\u2082-Emissionen eines Bindemittelsystems beitragen. Die tats\u00e4chliche Wirkung ist system- und bilanzierungsabh\u00e4ngig.' },
        { title: 'Erstarrungszeit', desc: 'Kann das Erstarrungsverhalten beeinflussen. Die Wirkung ist abh\u00e4ngig von Bindemittel, Dosierung, Zusatzmitteln und Temperatur.' },
      ],
    },
    reaction: {
      label: 'Reaktion mit Zement & Kalk',
      title: 'Wie Metakaolin mit Zement und Kalk reagiert',
      lead:
        'Portlandzement besteht zu etwa 80 % aus Calciumsilikaten \u2014 Alit (C\u2083S) und Belit (C\u2082S). Ihr Hydratationsverhalten erkl\u00e4rt, warum die puzzolanische Reaktion von Metakaolin so vorteilhaft ist.',
      image: 'powderMagnifier',
      block1Title: 'Hydratation des Portlandzements',
      block1Body:
        'Bei der Hydratation der Klinkerphasen entstehen vor allem C-S-H-Phasen sowie Portlandit (Calciumhydroxid, CH). Art und Mengenanteile der Hydratationsprodukte h\u00e4ngen von Zementzusammensetzung, Wasser-Bindemittel-Wert, Hydratationsalter und Zusatzstoffen ab.',
      eq1: ['C\u2083S + 3H \u2192 CSH + 2 CH', 'C\u2082S + 2H \u2192 CSH + CH'],
      block2Title: 'Die puzzolanische Reaktion',
      block2Body:
        'Das gebildete Calciumhydroxid (Portlandit, CH) kann mit Metakaolin puzzolanisch reagieren. Dabei k\u00f6nnen neben C-S-H- auch aluminiumhaltige Hydratphasen entstehen \u2013 etwa C-A-S-H-Phasen sowie Calciumaluminat- und Calciumsulfoaluminat-Hydrate wie Str\u00e4tlingit.',
      eq2: ['CH + S \u2192 CSH   (gelartige Strukturen)', 'CH + S + A \u2192 C\u2082ASH\u2088   (z. B. Str\u00e4tlingit)'],
      schematicNote: 'Vereinfachte, schematische Darstellung; die tats\u00e4chlich gebildeten Hydratphasen h\u00e4ngen von der chemischen Zusammensetzung und den Reaktionsbedingungen ab.',
      note:
        'Durch die puzzolanische Reaktion kann Metakaolin einen Teil des bei der Zementhydratation gebildeten Portlandits in zus\u00e4tzliche Hydratphasen \u00fcberf\u00fchren. Dies kann die Porenstruktur verfeinern und \u2013 abh\u00e4ngig von Rezeptur, Dosierung und Nachbehandlung \u2013 Festigkeit sowie Dauerhaftigkeit positiv beeinflussen.',
    },
    applications: {
      label: 'Anwendungen',
      title: 'Metakaolin-Anwendungen weltweit',
      lead:
        'Von gro\u00dftechnischer Infrastruktur \u00fcber feine architektonische Oberfl\u00e4chen bis zur Denkmalpflege \u2014 Metakaolin wird im gesamten Spektrum zement- und kalkgebundener Systeme eingesetzt.',
      groups: [
        { title: 'Zement', items: ['Unterst\u00fctzung der Fr\u00fchfestigkeitsentwicklung in geeigneten Rezepturen', 'Reduziertes Ausbl\u00fchungsrisiko in geeigneten Systemen', 'Bindemittelsysteme mit Potenzial f\u00fcr erh\u00f6hte Dauerhaftigkeit'] },
        { title: 'Ortbeton', items: ['Rezepturen f\u00fcr chlorid- und salzbeanspruchte Umgebungen \u2013 nach anwendungsbezogener Eignungspr\u00fcfung', 'Fahrbahndecken', 'Infrastrukturanwendungen mit erh\u00f6hter Dauerhaftigkeitsanforderung \u2013 nach projektbezogener Pr\u00fcfung'] },
        { title: 'Fertigteilbeton', items: ['Helle bis sehr helle Betonoberfl\u00e4chen \u2013 abh\u00e4ngig von Rohstofffarbe und Rezeptur', 'Farbiger Sichtbeton', 'Ausgew\u00e4hlte Fertigteilanwendungen nach werkseigener Pr\u00fcfung'] },
        { title: 'Spritzbeton', items: ['Spezialm\u00f6rtel und Spritzbetonsysteme f\u00fcr ausgew\u00e4hlte Infrastrukturprojekte', 'Hangsicherungs- und Instandsetzungssysteme \u2013 nach projektspezifischer Eignungspr\u00fcfung', 'Reparatur- und Verst\u00e4rkungsm\u00f6rtel nach anwendungsspezifischer Validierung'] },
        {
          title: 'M\u00f6rtel',
          items: [
            'Reparaturm\u00f6rtel',
            'Historische Kalkm\u00f6rtel',
            'Zement-/Kalkputze',
            'Reine Kalkputze',
            'Klebstoffe',
            'W\u00e4rmed\u00e4mmsysteme',
            'Ausbl\u00fchungsfreie M\u00f6rtel',
            'Schwimmb\u00e4der',
            'Wasserdichte Beschichtungen',
            'Fliesenfugen',
          ],
        },
        { title: 'Geopolymere', items: ['Alkalisch aktivierte Bindemittel', 'Klinkerreduzierte bzw. alkalisch aktivierte Bindemittelsysteme; die CO\u2082-Bilanz ist systemabh\u00e4ngig', 'Alkalisch aktivierte und hochtemperaturbest\u00e4ndige Spezialbindemittel \u2013 abh\u00e4ngig von Rezeptur und Pr\u00fcfanforderung'] },
      ],
    },
    cases: {
      label: 'Fallstudien',
      title: 'Metakaolin in realen Projekten',
      lead:
        'Ausgew\u00e4hlte Beispiele, die zeigen, wie Metakaolin in der Praxis eingesetzt wird. (Beispielhafte Darstellungen.)',
      list: [
        {
          title: 'Wei\u00dfe Architekturfassade',
          location: 'Mitteleuropa',
          desc: 'Fertigteil-Fassadenelemente mit Metakaolin lieferten eine helle, gleichm\u00e4\u00dfige Oberfl\u00e4che ohne Ausbl\u00fchungen und mit ausgezeichneter Dauerhaftigkeit.',
        },
        {
          title: 'Maritime Infrastruktur',
          location: 'Atlantikk\u00fcste',
          desc: 'Ortbetonelemente in der Spritzwasserzone profitierten von stark reduzierter Chloridmigration und verbesserter Sulfatbest\u00e4ndigkeit.',
        },
        {
          title: 'Denkmalgerechte Kalkrestaurierung',
          location: 'Historischer Stadtkern',
          desc: 'Mit Metakaolin modifizierte reine Kalkputze restaurierten ein denkmalgesch\u00fctztes Geb\u00e4ude und blieben mit dem originalen Untergrund kompatibel.',
        },
        {
          title: 'Schwimmbad-Abdichtung',
          location: 'S\u00fcdeuropa',
          desc: 'Wasserdichte Beschichtungen mit Metakaolin erzielten eine dichte, dauerhafte Oberfl\u00e4che, best\u00e4ndig gegen st\u00e4ndige Wassereinwirkung.',
        },
      ],
    },
    links: {
      label: 'Externe Links',
      title: 'Weiterf\u00fchrende Literatur & Referenzen',
      lead: 'Ausgew\u00e4hlte externe Ressourcen zu puzzolanischen Reaktionen, Zementchemie und Metakaolin.',
      list: [
        { label: 'Puzzolanische Reaktion', url: 'https://de.wikipedia.org/wiki/Puzzolan', desc: 'Wikipedia \u2014 die Chemie hinter puzzolanischen Zusatzstoffen.' },
        { label: 'Portlandzement', url: 'https://de.wikipedia.org/wiki/Portlandzement', desc: 'Wikipedia \u2014 Zusammensetzung und Hydratation von Portlandzement.' },
        { label: 'Calciumsilikathydrat (CSH)', url: 'https://en.wikipedia.org/wiki/Calcium_silicate_hydrate', desc: 'Wikipedia \u2014 die wichtigste festigkeitsbildende Phase im Beton.' },
        { label: 'Calciumhydroxid (Portlandit)', url: 'https://de.wikipedia.org/wiki/Calciumhydroxid', desc: 'Wikipedia \u2014 der freie Kalk, der durch die puzzolanische Reaktion verbraucht wird.' },
        { label: 'Beton', url: 'https://de.wikipedia.org/wiki/Beton', desc: 'Wikipedia \u2014 allgemeiner \u00dcberblick \u00fcber Beton als Baustoff.' },
        { label: 'Newchem GmbH', url: 'https://www.newchem.info', desc: 'Die Additiv-Spezialisten hinter dieser Plattform.' },
      ],
    },
    disclaimer: {
      label: 'Haftungsausschluss',
      title: 'Haftungsausschluss',
      body: [
        'Die auf dieser Website bereitgestellten Informationen \u00fcber Metakaolin sind allgemeiner, informativer Natur und wurden nach bestem Wissen und Gewissen zusammengestellt.',
        'Sie stellen keine Zusicherung bestimmter Eigenschaften oder der Eignung f\u00fcr eine bestimmte Anwendung dar. Werte wie Dosierung und Leistung h\u00e4ngen von der konkreten Metakaolin-Qualit\u00e4t, dem Bindemittelsystem, der Rezeptur und den Baustellenbedingungen ab.',
        'Nutzer m\u00fcssen die Eignung eines Produkts f\u00fcr die eigene Anwendung durch eigene Pr\u00fcfungen sicherstellen. F\u00fcr Sch\u00e4den, die aus der Verwendung dieser Informationen entstehen, wird keine Haftung \u00fcbernommen.',
        'F\u00fcr verbindliche technische Beratung, Produktauswahl und Lieferung wenden Sie sich bitte direkt an die Newchem GmbH.',
      ],
    },
    contact: {
      label: 'Kontakt',
      title: 'Sprechen Sie mit dem Newchem-Team',
      lead:
        'F\u00fcr technische Beratung zu Metakaolin-Qualit\u00e4ten, Dosierung, Rezepturentwicklung und Lieferung wenden Sie sich an unsere Spezialisten in \u00d6sterreich.',
      formTitle: 'Senden Sie uns eine Nachricht',
      name: 'Name',
      email: 'E-Mail',
      company: 'Unternehmen',
      message: 'Ihre Nachricht',
      send: 'Nachricht senden',
      sent: 'Vielen Dank! Ihre Nachricht wurde erfasst.',
      officeTitle: 'Newchem GmbH',
      addressTitle: 'Adresse',
      emailTitle: 'E-Mail',
      phoneTitle: 'Telefon',
      webTitle: 'Website',
    },
    footer: {
      tagline: 'Fachlich betreute Wissensplattform zu Metakaolin \u2014 herausgegeben von der Newchem GmbH.',
      quickLinks: 'Navigation',
      contactTitle: 'Kontakt',
      rights: 'Alle Rechte vorbehalten.',
      builtBy: 'Eine Plattform der',
      legal: 'Rechtliches',
    },
  },
};
